import { returnAllTradeDetailsFromGenericParams } from "@/lib/agGridColDefs/colDefGlobalHelpers";
import {
  containsHyphenInQuote,
  convertToDollarFraction,
  formatDollarFractionString,
  regexCheckFractionQuote,
} from "@/lib/businessRules/dollarFractionBusinessRule";
import { validateDollarFractionInput } from "@/lib/businessRules/dollarFractionInputValidation";
import { useTheme } from "next-themes";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { updateFillPriceInStore, getNotationFromStore, getFillPriceFromStore } from "./fillPriceHelpers";
import { truncateValueIfNeeded } from "../../cellEditors/DynamicCellEditorQuoteDollarFractionHelper";
import {
  isNumericallyZero,
  normalizeZeroQuote,
} from "@/lib/businessRules/zeroNormalizationHelper";

/**
 * FillPriceCellEditor
 *
 * Cell editor for the fill price field in the Order Fills table.
 * Only available for RP role trades.
 * Updates the price value in the fills array within tradeDataStore.
 * Supports $fr (dollar fraction) notation for fractional quote entry.
 */
const FillPriceCellEditor = forwardRef((params: any, ref) => {
  const { theme } = useTheme();
  const { fullTradeData } = returnAllTradeDetailsFromGenericParams(params);

  const fillId = params.data?.fillId;
  const blockId = params.data?.blockId;

  // Get notation to determine rounding rules
  const notation = fullTradeData?.esid
    ? getNotationFromStore(fullTradeData.esid, blockId)
    : undefined;
  const isUsingDollarFractionLogic = notation === "$fr";
  const inputType = isUsingDollarFractionLogic ? "text" : "number";

  const [value, setValue] = useState(() => {
    // Try to get value from store first (like renderer does), fallback to params.value
    let initialValue = params.value;
    if (fullTradeData?.esid && fillId !== undefined && blockId !== undefined) {
      const storeValue = getFillPriceFromStore(fullTradeData.esid, fillId, blockId);
      if (storeValue !== undefined) {
        initialValue = storeValue;
      }
    }
    return (initialValue ?? "").toString();
  });

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;

    if (input === "") {
      setValue(input);
      return;
    }

    // $fr: use dollar fraction input validation
    if (isUsingDollarFractionLogic) {
      if (validateDollarFractionInput(input)) {
        setValue(input);
      }
      return;
    }

    // Non-$fr: allow negative sign, digits, and decimal point
    if (!/^-?\d*\.?\d*$/.test(input)) return;

    setValue(input);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      params.stopEditing();
    } else if (e.key === "Escape") {
      params.stopEditing(true);
    }
  };

  const validateAndSave = () => {
    const esid = fullTradeData?.esid;
    const initialValue = params.value?.toString() || "";

    // Early return if value hasn't changed - don't apply rounding
    if (value === initialValue) {
      return value === "" ? undefined : value;
    }

    if (!esid || fillId === undefined || blockId === undefined) {
      return value === "" ? undefined : Number(value);
    }

    let finalValue: string | undefined;

    if (value === "") {
      finalValue = undefined;
    } else if (isNumericallyZero(value)) {
      // Collapse any all-zero form ("00", "0.00", "-0", "0-00", "-0-00", "00-000" …) to
      // the canonical zero for this notation, preserving a user-entered leading "-".
      finalValue = normalizeZeroQuote(value, notation);
      if (isUsingDollarFractionLogic) {
        updateFillPriceInStore(esid, fillId, blockId, finalValue);
        return finalValue;
      }
    } else if (isUsingDollarFractionLogic) {
      // $fr: validate, convert, and format
      if (!regexCheckFractionQuote({ newValue: value })) {
        setValue("");
        return "";
      }
      // If user typed a decimal, convert to fraction format
      if (!containsHyphenInQuote(value)) {
        const truncated = truncateValueIfNeeded(value, notation);
        const numVal = Number(truncated);
        if (!isNaN(numVal)) {
          finalValue = formatDollarFractionString(convertToDollarFraction(numVal));
        } else {
          finalValue = truncated;
        }
      } else {
        // User typed a fraction — just pad formatting
        finalValue = formatDollarFractionString(value);
      }
      // Store as string for $fr
      updateFillPriceInStore(esid, fillId, blockId, finalValue);
      return finalValue;
    } else {
      // Non-$fr: apply rounding based on notation
      const roundedValue = truncateValueIfNeeded(value, notation).replace(
        /,/g,
        "",
      );
      finalValue = roundedValue;
    }

    // Update the Zustand store with the rounded value
    const priceToStore = finalValue !== undefined ? Number(finalValue) : undefined;

    updateFillPriceInStore(esid, fillId, blockId, priceToStore);

    return finalValue;
  };

  useImperativeHandle(ref, () => ({
    getValue: () => validateAndSave(),
  }));

  return (
    <input
      ref={inputRef}
      value={value}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      className={`${
        theme === "light" ? "inputCellLight" : "inputCellDark"
      } ag-input ag-roundedInputStyle w-full justify-center !border-none text-center`}
      type={inputType}
      step={isUsingDollarFractionLogic ? undefined : "any"}
      data-testid="fillPriceCellEditor"
    />
  );
});

export default FillPriceCellEditor;
